import { useEffect, useState, useCallback } from 'react';
import {
    Modal, ModalHeader, ModalBody, ModalFooter,
    Form, FormGroup, Label, Input, Button, FormFeedback
} from 'reactstrap';
import Select from 'react-select';
import useCreateCliente from '../Hooks/useCreateCliente';
import useModalForm from '../Hooks/useModalForm';
import ConfirmCloseModal from '../Common/ConfirmCloseModal';
import ErrorAlert from '../Common/ErrorAlert';
import { isValidRUTFormat } from '../../utils/rut';

const initialFormState = {
    tperso: 'E',
    razonSocial: '',
    nombres: '',
    apellidoPaterno: '',
    apellidoMaterno: '',
    direccion: '',
    ciudad: '',
    comuna: '',
    fono: '',
    giro: '',
    email: '',
    sinEmail: false,
    calle: '',
    numero: '',
    depto: '',
    block: '',
    villaPoblacion: '',
    pais: '',
    contacto: '',
    fechaNacimiento: ''
};

function validateForm(fd) {
    const errors = {};
    const tperso = fd.tperso || 'E';

    if (!tperso) {
        errors.tperso = 'Este campo es requerido';
    }

    if (tperso === 'E') {
        if (!fd.razonSocial?.trim()) errors.razonSocial = 'Este campo es requerido';
    } else if (tperso === 'P') {
        if (!fd.nombres?.trim()) errors.nombres = 'Este campo es requerido';
        if (!fd.apellidoPaterno?.trim()) errors.apellidoPaterno = 'Este campo es requerido';
        if (!fd.apellidoMaterno?.trim()) errors.apellidoMaterno = 'Este campo es requerido';
    }

    if (!fd.direccion?.trim()) errors.direccion = 'Este campo es requerido';
    if (!fd.ciudad?.trim()) errors.ciudad = 'Este campo es requerido';
    if (!fd.comuna?.trim()) errors.comuna = 'Este campo es requerido';
    if (!fd.fono?.trim()) errors.fono = 'Este campo es requerido';
    if (!fd.giro?.trim()) errors.giro = 'Este campo es requerido';
    if (!fd.sinEmail && !fd.email?.trim()) errors.email = 'Este campo es requerido';

    return errors;
}

function buildSgMaerutData(fd, rut) {
    const tperso = fd.tperso || 'E';
    const normalizedRut = rut.replace(/[.]/g, '').trim();

    const sg_maerutData = {
        CLIPRO: normalizedRut,
        TPERSO: tperso,
        DIRECC: fd.direccion?.trim().toUpperCase() || null,
        CIUDAD: fd.ciudad?.trim().toUpperCase() || null,
        COMUNA: fd.comuna?.trim().toUpperCase() || null,
        NFONO1: fd.fono?.trim() || null,
        ELGIRO: fd.giro?.trim().toUpperCase() || null,
        CALLES: fd.calle?.trim().toUpperCase() || null,
        NROCAS: fd.numero?.trim() || null,
        NRODEP: fd.depto?.trim() || null,
        NROBLO: fd.block?.trim() || null,
        SECPOB: fd.villaPoblacion?.trim().toUpperCase() || null,
        COPAIS: fd.pais?.trim().toUpperCase() || null,
        CONTAC: fd.contacto?.trim().toUpperCase() || null,
        FECNAC: fd.fechaNacimiento || null,
    };

    if (tperso === 'E') {
        sg_maerutData.RAZSOC = fd.razonSocial?.trim().toUpperCase() || null;
    } else if (tperso === 'P') {
        sg_maerutData.NOMNOM = fd.nombres?.trim().toUpperCase() || null;
        sg_maerutData.APEPAT = fd.apellidoPaterno?.trim().toUpperCase() || null;
        sg_maerutData.APEMAT = fd.apellidoMaterno?.trim().toUpperCase() || null;
    }

    if (fd.sinEmail) {
        sg_maerutData.SINEMAIL = 1;
        sg_maerutData.CEMAIL = null;
    } else {
        sg_maerutData.SINEMAIL = 0;
        sg_maerutData.CEMAIL = fd.email?.trim().toLowerCase() || null;
    }

    return sg_maerutData;
}

const ModalCrearRut = ({ isOpen, toggle, rut, onSave }) => {
    const { createCliente } = useCreateCliente();
    const [validationErrors, setValidationErrors] = useState({});

    const { formData, setFormData, resetForm, loading, error, isDirty, handleSubmit, handleCancel, showConfirm, setShowConfirm } = useModalForm({
        initialData: initialFormState,
        onSave: async (fd) => {
            const errors = validateForm(fd);
            if (Object.keys(errors).length > 0) {
                setValidationErrors(errors);
                throw new Error('Complete los campos requeridos');
            }

            if (!isValidRUTFormat(rut)) {
                throw new Error('El RUT debe tener formato XXXXXXXX-X (sin puntos, con guión)');
            }

            const sg_maerutData = buildSgMaerutData(fd, rut);
            const result = await createCliente(sg_maerutData);

            if (!result.success) {
                throw new Error(result.error || 'Error al crear el cliente');
            }

            const normalizedRut = rut.replace(/[.-]/g, '').trim();
            onSave({ CLIPRO: normalizedRut });
        },
        onClose: toggle,
    });

    useEffect(() => {
        if (isOpen) {
            resetForm();
            setValidationErrors({});
        }
    }, [isOpen, resetForm]);

    const handleInputChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        }
    }, [setFormData, validationErrors]);

    const handleConfirmClose = useCallback(() => {
        setShowConfirm(false);
        toggle();
    }, [setShowConfirm, toggle]);

    const tperso = formData.tperso || 'E';

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" backdrop="static">
            <ModalHeader toggle={toggle}>
                <i className="ri-user-add-line me-2"></i>
                Crear rut
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {error && (
                        <ErrorAlert error={error} onDismiss={() => {}} />
                    )}
                    <div className="row">
                        <div className="col-6">
                            <FormGroup>
                                <Label for="rutField">RUT</Label>
                                <Input
                                    type="text"
                                    id="rutField"
                                    name="rutField"
                                    value={rut}
                                    disabled
                                />
                            </FormGroup>
                        </div>
                        <div className="col-6">
                            <FormGroup>
                                <Label for="tperso">Tipo Persona *</Label>
                                <Select
                                    id="tperso"
                                    name="tperso"
                                    value={tperso ? { value: tperso, label: tperso === 'P' ? 'Persona Natural' : 'Persona Jurídica' } : null}
                                    onChange={(option) => {
                                        handleInputChange({ target: { name: 'tperso', value: option ? option.value : '' } });
                                    }}
                                    options={[
                                        { value: 'E', label: 'Persona Jurídica' },
                                        { value: 'P', label: 'Persona Natural' }
                                    ]}
                                    isSearchable
                                    isClearable
                                    placeholder="Seleccionar..."
                                />
                                {validationErrors.tperso && (
                                    <div className="text-danger" style={{ fontSize: '0.875em', marginTop: '0.25rem' }}>{validationErrors.tperso}</div>
                                )}
                            </FormGroup>
                        </div>
                    </div>

                    <div className="d-flex align-items-center my-4">
                        <div className="flex-grow-1 border-top"></div>
                        <span className="mx-3 text-muted">Identificación</span>
                        <div className="flex-grow-1 border-top"></div>
                    </div>

                    {tperso === 'E' ? (
                        <>
                            <div className="row">
                                <div className="col-12">
                                    <FormGroup>
                                        <Label for="razonSocial">Razón Social</Label>
                                        <Input
                                            type="text"
                                            id="razonSocial"
                                            name="razonSocial"
                                            value={formData.razonSocial}
                                            onChange={handleInputChange}
                                            placeholder="Ej: Siga Ltda"
                                            invalid={!!validationErrors.razonSocial}
                                        />
                                        {validationErrors.razonSocial && (
                                            <FormFeedback>{validationErrors.razonSocial}</FormFeedback>
                                        )}
                                    </FormGroup>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="row">
                                <div className="col-12">
                                    <FormGroup>
                                        <Label for="nombres">Nombres</Label>
                                        <Input
                                            type="text"
                                            id="nombres"
                                            name="nombres"
                                            value={formData.nombres}
                                            onChange={handleInputChange}
                                            placeholder="Ej: Jorge Miguel"
                                            invalid={!!validationErrors.nombres}
                                        />
                                        {validationErrors.nombres && (
                                            <FormFeedback>{validationErrors.nombres}</FormFeedback>
                                        )}
                                    </FormGroup>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="apellidoPaterno">Ap. Paterno</Label>
                                        <Input
                                            type="text"
                                            id="apellidoPaterno"
                                            name="apellidoPaterno"
                                            value={formData.apellidoPaterno}
                                            onChange={handleInputChange}
                                            placeholder="Ej: González"
                                            invalid={!!validationErrors.apellidoPaterno}
                                        />
                                        {validationErrors.apellidoPaterno && (
                                            <FormFeedback>{validationErrors.apellidoPaterno}</FormFeedback>
                                        )}
                                    </FormGroup>
                                </div>
                                <div className="col-6">
                                    <FormGroup>
                                        <Label for="apellidoMaterno">Ap. Materno</Label>
                                        <Input
                                            type="text"
                                            id="apellidoMaterno"
                                            name="apellidoMaterno"
                                            value={formData.apellidoMaterno}
                                            onChange={handleInputChange}
                                            placeholder="Ej: Tapia"
                                            invalid={!!validationErrors.apellidoMaterno}
                                        />
                                        {validationErrors.apellidoMaterno && (
                                            <FormFeedback>{validationErrors.apellidoMaterno}</FormFeedback>
                                        )}
                                    </FormGroup>
                                </div>
                            </div>
                        </>
                    )}

                    <div className="d-flex align-items-center my-4">
                        <div className="flex-grow-1 border-top"></div>
                        <span className="mx-3 text-muted">Dirección</span>
                        <div className="flex-grow-1 border-top"></div>
                    </div>

                    <div className="row">
                        <div className="col-12">
                            <FormGroup>
                                <Label for="direccion">Dirección</Label>
                                <Input
                                    type="text"
                                    id="direccion"
                                    name="direccion"
                                    value={formData.direccion}
                                    onChange={handleInputChange}
                                    placeholder="Ej: Arturo Prat 390 Of. 1302"
                                    invalid={!!validationErrors.direccion}
                                />
                                {validationErrors.direccion && (
                                    <FormFeedback>{validationErrors.direccion}</FormFeedback>
                                )}
                            </FormGroup>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <FormGroup>
                                <Label for="ciudad">Ciudad</Label>
                                <Input
                                    type="text"
                                    id="ciudad"
                                    name="ciudad"
                                    value={formData.ciudad}
                                    onChange={handleInputChange}
                                    placeholder="Ej: Concepción"
                                    invalid={!!validationErrors.ciudad}
                                />
                                {validationErrors.ciudad && (
                                    <FormFeedback>{validationErrors.ciudad}</FormFeedback>
                                )}
                            </FormGroup>
                        </div>
                        <div className="col-6">
                            <FormGroup>
                                <Label for="comuna">Comuna</Label>
                                <Input
                                    type="text"
                                    id="comuna"
                                    name="comuna"
                                    value={formData.comuna}
                                    onChange={handleInputChange}
                                    placeholder="Ej: Biobío"
                                    invalid={!!validationErrors.comuna}
                                />
                                {validationErrors.comuna && (
                                    <FormFeedback>{validationErrors.comuna}</FormFeedback>
                                )}
                            </FormGroup>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-3">
                            <FormGroup>
                                <Label for="calle">Calle</Label>
                                <Input
                                    type="text"
                                    id="calle"
                                    name="calle"
                                    value={formData.calle}
                                    onChange={handleInputChange}
                                    placeholder="Calle"
                                />
                            </FormGroup>
                        </div>
                        <div className="col-3">
                            <FormGroup>
                                <Label for="numero">N°</Label>
                                <Input
                                    type="text"
                                    id="numero"
                                    name="numero"
                                    value={formData.numero}
                                    onChange={handleInputChange}
                                    placeholder="N°"
                                />
                            </FormGroup>
                        </div>
                        <div className="col-3">
                            <FormGroup>
                                <Label for="depto">Depto</Label>
                                <Input
                                    type="text"
                                    id="depto"
                                    name="depto"
                                    value={formData.depto}
                                    onChange={handleInputChange}
                                    placeholder="Depto"
                                />
                            </FormGroup>
                        </div>
                        <div className="col-3">
                            <FormGroup>
                                <Label for="block">Block</Label>
                                <Input
                                    type="text"
                                    id="block"
                                    name="block"
                                    value={formData.block}
                                    onChange={handleInputChange}
                                    placeholder="Block"
                                />
                            </FormGroup>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <FormGroup>
                                <Label for="villaPoblacion">Villa/Pobl.</Label>
                                <Input
                                    type="text"
                                    id="villaPoblacion"
                                    name="villaPoblacion"
                                    value={formData.villaPoblacion}
                                    onChange={handleInputChange}
                                    placeholder="Villa o Población"
                                />
                            </FormGroup>
                        </div>
                        <div className="col-6">
                            <FormGroup>
                                <Label for="pais">País</Label>
                                <Input
                                    type="text"
                                    id="pais"
                                    name="pais"
                                    value={formData.pais}
                                    onChange={handleInputChange}
                                    placeholder="Ej: Chile"
                                />
                            </FormGroup>
                        </div>
                    </div>

                    <div className="d-flex align-items-center my-4">
                        <div className="flex-grow-1 border-top"></div>
                        <span className="mx-3 text-muted">Contacto</span>
                        <div className="flex-grow-1 border-top"></div>
                    </div>

                    <div className="row">
                        <div className="col-6">
                            <FormGroup>
                                <Label for="fono">Fono</Label>
                                <Input
                                    type="text"
                                    id="fono"
                                    name="fono"
                                    value={formData.fono}
                                    onChange={handleInputChange}
                                    placeholder="Ej: 412345678"
                                    invalid={!!validationErrors.fono}
                                />
                                {validationErrors.fono && (
                                    <FormFeedback>{validationErrors.fono}</FormFeedback>
                                )}
                            </FormGroup>
                        </div>
                        <div className="col-6">
                            <FormGroup>
                                <Label for="giro">Giro</Label>
                                <Input
                                    type="text"
                                    id="giro"
                                    name="giro"
                                    value={formData.giro}
                                    onChange={handleInputChange}
                                    placeholder="Ej: Consultoría Informática"
                                    invalid={!!validationErrors.giro}
                                />
                                {validationErrors.giro && (
                                    <FormFeedback>{validationErrors.giro}</FormFeedback>
                                )}
                            </FormGroup>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <FormGroup>
                                <Label for="email">Email</Label>
                                {!formData.sinEmail && (
                                    <Input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        placeholder="Ej: correo@ejemplo.cl"
                                        invalid={!!validationErrors.email}
                                    />
                                )}
                                {validationErrors.email && (
                                    <FormFeedback>{validationErrors.email}</FormFeedback>
                                )}
                            </FormGroup>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-4">
                            <FormGroup>
                                <Label for="contacto">Contacto</Label>
                                <Input
                                    type="text"
                                    id="contacto"
                                    name="contacto"
                                    value={formData.contacto}
                                    onChange={handleInputChange}
                                    placeholder="Nombre contacto"
                                />
                            </FormGroup>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <FormGroup>
                                <Label for="fechaNacimiento">Fecha Nac.</Label>
                                <Input
                                    type="date"
                                    id="fechaNacimiento"
                                    name="fechaNacimiento"
                                    value={formData.fechaNacimiento}
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </div>
                        <div className="col-6 d-flex align-items-end">
                            <FormGroup check className="mb-3">
                                <Label check for="sinEmail">
                                    <Input
                                        type="checkbox"
                                        id="sinEmail"
                                        name="sinEmail"
                                        checked={formData.sinEmail}
                                        onChange={handleInputChange}
                                    />{' '}
                                    Cliente sin Email
                                </Label>
                            </FormGroup>
                        </div>
                    </div>

                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="light" onClick={handleCancel} disabled={loading}>
                        Cancelar
                    </Button>
                    <Button type="submit" color="success" disabled={loading}>
                        {loading ? (
                            <>
                                <i className="ri-loader-4-line ri-spin-line me-1"></i>
                                Guardando...
                            </>
                        ) : (
                            <>
                                <i className="ri-save-line me-1"></i>
                                Crear
                            </>
                        )}
                    </Button>
                </ModalFooter>
            </Form>
            <ConfirmCloseModal
                isOpen={showConfirm}
                toggle={() => setShowConfirm(false)}
                onConfirm={handleConfirmClose}
            />
        </Modal>
    );
};

export default ModalCrearRut;
